import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../api/apiClient';
import Breadcrumb from '../ui/Breadcrumb';
import Modal from '../ui/Modal';
import { moduleColor } from '../ui/modulePalette';
import { IconAlertTriangle, IconBan, IconCheck, IconClock, IconEdit, IconGlobe, IconKey, IconPlus, IconTrash, IconUsers } from '../ui/icons';
import PublicAppointmentPage from './PublicAppointmentPage';

function contactTitle(c) {
  const company = String(c?.company_name || '').trim();
  const name = `${String(c?.first_name || '').trim()} ${String(c?.last_name || '').trim()}`.trim();
  return company || name || String(c?.email || '').trim() || `Contact #${c?.id ?? ''}`;
}

function contactAddress(c) {
  const cc = c && typeof c === 'object' ? c : null;
  if (!cc) return '';
  const parts = [
    String(cc.address_line1 || '').trim(),
    String(cc.address_line2 || '').trim(),
    `${String(cc.postal_code || '').trim()} ${String(cc.city || '').trim()}`.trim(),
    String(cc.country || '').trim(),
  ].filter(Boolean);
  return parts.join(', ');
}

function formatDateTime(input) {
  const raw = String(input || '').trim();
  if (!raw) return '—';
  const d = new Date(raw.replace(' ', 'T'));
  if (!Number.isFinite(d.getTime())) return raw;
  try {
    return new Intl.DateTimeFormat('fr-FR', { dateStyle: 'full', timeStyle: 'short' }).format(d);
  } catch {
    return d.toLocaleString();
  }
}

function defaultDateTimeLocal() {
  const now = new Date();
  const next = new Date(now.getTime() + 60 * 60 * 1000);
  const pad = (n) => String(n).padStart(2, '0');
  const yyyy = next.getFullYear();
  const mm = pad(next.getMonth() + 1);
  const dd = pad(next.getDate());
  const hh = pad(next.getHours());
  const min = pad(next.getMinutes());
  return { date: `${yyyy}-${mm}-${dd}`, time: `${hh}:${min}` };
}

function splitStartAt(startAt) {
  const raw = String(startAt || '').trim();
  if (!raw) return { date: '', time: '' };
  const date = raw.slice(0, 10);
  const time = raw.slice(11, 16);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return { date: '', time: '' };
  if (!/^\d{2}:\d{2}$/.test(time)) return { date: '', time: '' };
  return { date, time };
}

function clientHasSite(client, siteId) {
  const sid = Number(siteId);
  return (client?.sites || []).some((s) => Number(s?.id) === sid);
}

function moduleIsAdmin(modules, moduleKey) {
  const key = String(moduleKey || '').trim();
  if (!key) return false;
  const list = Array.isArray(modules) ? modules : [];
  const found = list.find((m) => String(m?.key || '') === key) || null;
  return Boolean(found?.is_admin);
}

function normalizePublicToken(raw) {
  const t = String(raw || '').trim().toLowerCase();
  if (!/^[a-f0-9]{32,64}$/i.test(t)) return '';
  return t;
}

function locationRequiresAddress(locationType) {
  const t = String(locationType || '');
  return t === 'client' || t === 'other';
}

function parseContactAddressCandidate(raw) {
  let text = String(raw || '').trim();
  text = text.replace(/^Chez le contact\s*[—-]\s*/i, '').trim();
  const parts = text.split(',').map((x) => x.trim()).filter(Boolean);
  const out = {
    address_line1: '',
    address_line2: '',
    postal_code: '',
    city: '',
    country: '',
  };
  if (!parts.length) return out;

  out.address_line1 = parts[0] || '';
  if (parts.length > 2) out.address_line2 = parts[1] || '';

  const postalCityPart = parts.find((p) => /^\d{5}\s+/.test(p)) || parts[1] || '';
  const m = postalCityPart.match(/^(\d{5})\s+(.+)$/);
  if (m) {
    out.postal_code = String(m[1] || '').trim();
    out.city = String(m[2] || '').trim();
  }
  if (!out.city && parts[2]) out.city = parts[2];
  if (parts.length >= 3) out.country = parts[parts.length - 1] || '';
  return out;
}

export default function AppointmentsPage({ siteId, site, user, modules, routeRest, initialPublicToken, onNavigate }) {
  const [state, setState] = useState({ status: 'loading', error: '', appointments: [] });
  const [contacts, setContacts] = useState({ status: 'idle', list: [], error: '' });
  const [modal, setModal] = useState(null);
  const [quickActionId, setQuickActionId] = useState(null);
  const [addressAutocomplete, setAddressAutocomplete] = useState({
    status: 'idle',
    q: '',
    items: [],
  });

  const canAdminOps = useMemo(() => {
    const isSuperadmin = Boolean(String(user?.role || '') === 'admin' && user?.is_superadmin);
    if (isSuperadmin) return true;
    return moduleIsAdmin(modules, 'appointments');
  }, [user, modules]);

  const accent = moduleColor('appointments');

  function notifyAppointmentsChange(eventType = 'changed') {
    try {
      window.dispatchEvent(
        new CustomEvent('omniscope:moduleEvent', {
          detail: {
            moduleKey: 'appointments',
            siteId: Number(siteId || 0),
            type: String(eventType || 'changed'),
            at: Date.now(),
          },
        })
      );
    } catch {
      // ignore
    }
  }

  function logMailDebug(label, mail) {
    const m = mail && typeof mail === 'object' ? mail : null;
    if (!m) return;
    try {
      const ok = Boolean(m.ok);
      const transport = String(m.transport || '');
      const msgId = m?.meta?.message_id ? String(m.meta.message_id) : '';
      const qid = m.smtp_queue_id ? String(m.smtp_queue_id) : '';
      const title = `[mail] ${label} — ${ok ? 'OK' : 'FAIL'}${transport ? ` (${transport})` : ''}${qid ? ` — ${qid}` : ''}${msgId ? ` — ${msgId}` : ''}`;
      (console.groupCollapsed || console.group).call(console, title);
      if (m.meta) console.log('meta', m.meta);
      if (qid) console.log('smtp_queue_id', qid);
      if (m.smtp_response) console.log('smtp_response', m.smtp_response);
      if (Array.isArray(m.trace)) console.table(m.trace);
      if (!ok && m.error) console.warn('error', m.error);
      console.groupEnd();
    } catch {
      // ignore
    }
  }

  const refresh = useCallback(() => {
    if (!siteId) return;
    let cancelled = false;
    setState((s) => ({ ...s, status: 'loading', error: '' }));
    api
      .appointmentsList(Number(siteId), { limit: 300 })
      .then((res) => {
        if (cancelled) return;
        if (!res.ok) {
          setState({ status: 'error', error: res.error || 'Impossible de charger', appointments: [] });
          return;
        }
        setState({ status: 'ready', error: '', appointments: res.appointments || [] });
      })
      .catch(() => {
        if (cancelled) return;
        setState({ status: 'error', error: 'Impossible de charger', appointments: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [siteId]);

  useEffect(() => refresh(), [refresh]);

  useEffect(() => {
    const fromRoute = (() => {
      const a = Array.isArray(routeRest) ? routeRest : [];
      if (a[0] !== 'open') return '';
      return normalizePublicToken(a[1] || '');
    })();
    const fromProp = normalizePublicToken(initialPublicToken || '');
    const token = fromRoute || fromProp;
    if (!token) return;
    setModal((m) => {
      if (m?.type === 'public' && String(m.token || '') === token) return m;
      return { type: 'public', token };
    });
    if (fromRoute) onNavigate?.('/appointments');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeRest, initialPublicToken]);

  useEffect(() => {
    if (!canAdminOps || !siteId) {
      setContacts({ status: 'idle', list: [], error: '' });
      return;
    }
    let cancelled = false;
    setContacts({ status: 'loading', list: [], error: '' });
    api
      .adminClients()
      .then((res) => {
        if (cancelled) return;
        if (!res.ok) {
          setContacts({ status: 'error', list: [], error: res.error || 'Impossible de charger les contacts' });
          return;
        }
        const list = (res.clients || []).filter((c) => clientHasSite(c, siteId));
        list.sort((a, b) => contactTitle(a).localeCompare(contactTitle(b), 'fr'));
        setContacts({ status: 'ready', list, error: '' });
      })
      .catch(() => {
        if (cancelled) return;
        setContacts({ status: 'error', list: [], error: 'Impossible de charger les contacts' });
      });
    return () => {
      cancelled = true;
    };
  }, [canAdminOps, siteId]);

  useEffect(() => {
    const m = modal;
    if (!m || (m.type !== 'new' && m.type !== 'edit')) {
      setAddressAutocomplete({ status: 'idle', q: '', items: [] });
      return undefined;
    }
    if (String(m.location_type || '') !== 'client') {
      setAddressAutocomplete({ status: 'idle', q: '', items: [] });
      return undefined;
    }
    const q = String(m.location_label || '').trim();
    if (q.length < 4) {
      setAddressAutocomplete({ status: 'idle', q, items: [] });
      return undefined;
    }

    const ctrl = new AbortController();
    const timer = setTimeout(async () => {
      setAddressAutocomplete((s) => ({ ...s, status: 'loading', q }));
      try {
        const u = new URL('https://api-adresse.data.gouv.fr/search/');
        u.searchParams.set('q', q);
        u.searchParams.set('limit', '6');
        const res = await fetch(u.toString(), {
          method: 'GET',
          signal: ctrl.signal,
        });
        if (!res.ok) {
          setAddressAutocomplete({ status: 'error', q, items: [] });
          return;
        }
        const data = await res.json().catch(() => null);
        const feats = Array.isArray(data?.features) ? data.features : [];
        const items = feats
          .map((f) => String(f?.properties?.label || '').trim())
          .filter(Boolean)
          .slice(0, 6);
        setAddressAutocomplete({ status: 'ready', q, items });
      } catch {
        if (ctrl.signal.aborted) return;
        setAddressAutocomplete({ status: 'error', q, items: [] });
      }
    }, 240);

    return () => {
      clearTimeout(timer);
      ctrl.abort();
    };
  }, [modal]);

  const grouped = useMemo(() => {
    const list = state.appointments || [];

    function parseStart(a) {
      const raw = String(a?.start_at || '').trim();
      if (!raw) return NaN;
      const t = Date.parse(raw.replace(' ', 'T'));
      return Number.isFinite(t) ? t : NaN;
    }

    function parseUpdated(a) {
      const raw = String(a?.updated_at || a?.created_at || '').trim();
      const t = Date.parse(raw.replace(' ', 'T'));
      return Number.isFinite(t) ? t : 0;
    }

    const scheduled = list.filter((a) => String(a?.status || 'scheduled') === 'scheduled');
    const postponed = list.filter((a) => String(a?.status || '') === 'postponed');
    const cancelled = list.filter((a) => String(a?.status || '') === 'cancelled');
    const other = list.filter((a) => !scheduled.includes(a) && !postponed.includes(a) && !cancelled.includes(a));

    const now = Date.now();
    const threshold = now - 5 * 60 * 1000;

    const future = scheduled.filter((a) => {
      const t = parseStart(a);
      return Number.isFinite(t) && t >= threshold;
    });
    const past = scheduled.filter((a) => {
      const t = parseStart(a);
      return Number.isFinite(t) && t < threshold;
    });
    const undated = scheduled.filter((a) => !Number.isFinite(parseStart(a)));

    future.sort((a, b) => parseStart(a) - parseStart(b));
    past.sort((a, b) => parseStart(b) - parseStart(a));
    undated.sort((a, b) => parseUpdated(b) - parseUpdated(a));
    postponed.sort((a, b) => parseUpdated(b) - parseUpdated(a));
    cancelled.sort((a, b) => parseUpdated(b) - parseUpdated(a));
    other.sort((a, b) => parseUpdated(b) - parseUpdated(a));

    return { future, postponed, past, cancelled, undated, other };
  }, [state.appointments]);

  function openNewAppointment() {
    const dt = defaultDateTimeLocal();
    const first = contacts.list?.[0] || null;
    setModal({
      type: 'new',
      saving: false,
      error: '',
      client_id: first ? String(first.id) : '',
      date: dt.date,
      time: dt.time,
      location_type: 'agency',
      location_label: '',
    });
  }

  function openEditAppointment(appointment) {
    const a = appointment || {};
    const status = String(a.status || 'scheduled');
    const dt = splitStartAt(a.start_at);
    setModal({
      type: 'edit',
      saving: false,
      error: '',
      id: Number(a.id),
      client: a.client || null,
      status,
      date: dt.date,
      time: dt.time,
      postponed_with_date: status === 'postponed' && Boolean(dt.date && dt.time),
      location_type: String(a.location_type || 'agency'),
      location_label: String(a.location_label || ''),
    });
  }

  function openCounterProposal(appointment) {
    const a = appointment || {};
    const dt = splitStartAt(a.start_at);
    const fallback = defaultDateTimeLocal();
    setModal({
      type: 'edit',
      saving: false,
      error: '',
      id: Number(a.id),
      client: a.client || null,
      status: 'postponed',
      date: dt.date || fallback.date,
      time: dt.time || fallback.time,
      postponed_with_date: true,
      location_type: String(a.location_type || 'agency'),
      location_label: String(a.location_label || ''),
    });
  }

  function openPublicAppointment(token) {
    const t = String(token || '').trim();
    if (!/^[a-f0-9]{32,64}$/i.test(t)) {
      setModal({ type: 'email-warning', error: 'Lien public introuvable pour ce rendez-vous.' });
      return;
    }
    setModal({ type: 'public', token: t.toLowerCase() });
  }

  function resolveSelectedContact(clientId) {
    const id = Number(clientId);
    return (contacts.list || []).find((c) => Number(c.id) === id) || null;
  }

  function maybeOpenAddressLinkModal(contact, locationType, locationLabel) {
    const c = contact && typeof contact === 'object' ? contact : null;
    if (!c) return false;
    if (String(user?.role || '') !== 'admin') return false;
    if (!locationRequiresAddress(locationType)) return false;
    const parsed = parseContactAddressCandidate(locationLabel);
    if (!parsed.address_line1 && !parsed.postal_code && !parsed.city) return false;
    const existing = {
      address_line1: String(c.address_line1 || '').trim(),
      address_line2: String(c.address_line2 || '').trim(),
      postal_code: String(c.postal_code || '').trim(),
      city: String(c.city || '').trim(),
      country: String(c.country || '').trim(),
    };
    if (
      existing.address_line1 === parsed.address_line1 &&
      existing.address_line2 === parsed.address_line2 &&
      existing.postal_code === parsed.postal_code &&
      existing.city === parsed.city &&
      existing.country === parsed.country
    ) {
      return false;
    }
    setModal({
      type: 'link-address',
      saving: false,
      error: '',
      contact: c,
      appointment_location: String(locationLabel || ''),
      ...parsed,
    });
    return true;
  }

  async function confirmCreateAppointment({ client_id, date, time, location_type, location_label, email_to, attach_email_to_contact }) {
    const payload = {
      site_id: Number(siteId),
      client_id: Number(client_id),
      date,
      time,
      location_type,
      location_label,
      email_to,
      attach_email_to_contact: Boolean(attach_email_to_contact),
    };
    const res = await api.appointmentsCreate(payload);
    if (!res.ok) return res;
    if (res.email) logMailDebug('Rendez-vous — confirmation', res.email);
    await refresh();
    notifyAppointmentsChange('created');
    return res;
  }

  async function saveAppointmentEdit(m) {
    const id = Number(m?.id);
    if (!Number.isFinite(id) || id <= 0) return { ok: false, error: 'Rendez-vous invalide' };
    const status = String(m?.status || 'scheduled');
    const payload = {
      site_id: Number(siteId),
      id,
      status,
      location_type: String(m?.location_type || 'agency'),
      location_label: String(m?.location_label || ''),
    };
    if (status === 'scheduled' || (status === 'postponed' && Boolean(m?.postponed_with_date))) {
      payload.date = String(m?.date || '').trim();
      payload.time = String(m?.time || '').trim();
    }
    const res = await api.appointmentsUpdate(payload);
    if (res?.ok && res.email) logMailDebug('Rendez-vous — modification/annulation', res.email);
    if (res?.ok) notifyAppointmentsChange('updated');
    return res;
  }

  async function confirmProposedDate(appointment) {
    const a = appointment || null;
    const id = Number(a?.id);
    if (!Number.isFinite(id) || id <= 0) return;
    const dt = splitStartAt(a?.start_at);
    if (!dt.date || !dt.time) {
      openEditAppointment(a);
      return;
    }
    setQuickActionId(id);
    const res = await api.appointmentsUpdate({
      site_id: Number(siteId),
      id,
      status: 'scheduled',
      date: dt.date,
      time: dt.time,
      location_type: String(a?.location_type || 'agency'),
      location_label: String(a?.location_label || ''),
    });
    setQuickActionId(null);
    if (!res?.ok) {
      setState((s) => ({ ...s, error: res?.error || 'Confirmation impossible' }));
      return;
    }
    if (res.email) logMailDebug('Rendez-vous — confirmation', res.email);
    notifyAppointmentsChange('updated');
    await refresh();
  }

  if (!siteId) {
    return (
      <div className="Page">
        <div className="Card">
          <div className="TileTitle">Rendez-vous</div>
          <div className="Muted">Sélectionne un site.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="Page">
      <header className="PageHeader">
        <div className="PageHeaderBar">
          <Breadcrumb
            items={[{ label: 'Rendez-vous' }]}
            badges={
              <span className="Badge" style={{ borderColor: accent, color: accent }}>
                <span className="ButtonInline" style={{ gap: 8 }}>
                  <IconClock /> {site?.name || 'Site'}
                </span>
              </span>
            }
          />
          <div className="Badges">
            <button
              type="button"
              className="Button"
              style={{
                background: `color-mix(in srgb, ${accent} 60%, var(--panel))`,
                borderColor: `color-mix(in srgb, ${accent} 40%, var(--border))`,
              }}
              disabled={!canAdminOps || contacts.status === 'loading'}
              onClick={openNewAppointment}
              title={canAdminOps ? 'Planifier un rendez-vous' : 'Droits admin requis'}
            >
              <span className="ButtonInline">
                <IconPlus /> Nouveau
              </span>
            </button>
          </div>
        </div>
      </header>

      {state.error ? (
        <div className="Card" style={{ marginBottom: 14 }}>
          <div className="Error" role="alert">
            {state.error}
          </div>
        </div>
      ) : null}

      {!canAdminOps ? (
        <div className="Notice" style={{ marginBottom: 14 }}>
          <span className="ButtonInline" style={{ gap: 10 }}>
            <IconKey /> Accès limité : création/édition admin désactivées.
          </span>
          <div className="Muted" style={{ marginTop: 6 }}>
            Tu peux quand même modifier/annuler via le lien public d’un rendez-vous.
          </div>
        </div>
      ) : null}

      {contacts.error ? (
        <div className="Card" style={{ marginBottom: 14 }}>
          <div className="Error" role="alert">
            {contacts.error}
          </div>
        </div>
      ) : null}

      <div className="Card">
        <div className="Row" style={{ marginBottom: 12 }}>
          <div className="TileTitle">À venir</div>
        </div>

        {state.status === 'loading' ? <div className="Muted">Chargement…</div> : null}

          {state.status === 'ready' && grouped.future.length === 0 ? <div className="Muted">Aucun rendez-vous à venir.</div> : null}

        {state.status === 'ready' ? (
          <div className="List" role="list">
            {grouped.future.map((a) => {
              const mailOk = Boolean(a.email_sent_at) && !String(a.email_last_error || '').trim();
              const hasPublic = Boolean(String(a.public_token || '').trim());
              return (
                <div key={a.id} className="ListItem" role="listitem">
                  <div style={{ minWidth: 0 }}>
                    <div className="UserName" style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                      <span className="Badge" style={{ borderColor: accent, color: accent }}>
                        <span className="ButtonInline" style={{ gap: 8 }}>
                          <IconClock /> {formatDateTime(a.start_at)}
                        </span>
                      </span>
                      <span className="Badge">Planifié</span>
                      <span className="OneLine" style={{ minWidth: 0 }}>
                        {contactTitle(a.client)}
                      </span>
                    </div>
                    <div className="Muted" style={{ marginTop: 6 }}>
                      <span className="ButtonInline" style={{ gap: 8 }}>
                        <IconGlobe /> {a.location_label || '—'}
                      </span>
                      {a.email_to ? (
                        <>
                          {' '}
                          ·{' '}
                          <span className="ButtonInline" style={{ gap: 8 }}>
                            <IconUsers /> {a.email_to}
                          </span>
                        </>
                      ) : null}
                    </div>
                  </div>
                  <div className="Badges">
                    {a.email_to ? (
                      <span className="Badge" style={{ borderColor: mailOk ? 'var(--good)' : 'var(--danger)', color: mailOk ? 'var(--good)' : 'var(--danger)' }}>
                        <span className="ButtonInline" style={{ gap: 8 }}>
                          {mailOk ? <IconCheck /> : <IconAlertTriangle />} {mailOk ? 'Email envoyé' : 'Email non envoyé'}
                        </span>
                      </span>
                    ) : null}
                  </div>
                  {canAdminOps ? (
                    <div className="Badges">
                      <button type="button" className="Button Button--ghost" onClick={() => openEditAppointment(a)}>
                        <span className="ButtonInline">
                          <IconEdit /> Modifier
                        </span>
                      </button>
                    </div>
                  ) : (
                    <div className="Badges">
                      <button type="button" className="Button Button--ghost" disabled={!hasPublic} onClick={() => openPublicAppointment(a.public_token)}>
                        <span className="ButtonInline">
                          <IconEdit /> Modifier
                        </span>
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : null}
      </div>

      {state.status === 'ready' && grouped.postponed.length ? (
        <div className="Card" style={{ marginTop: 12 }}>
          <div className="Row" style={{ marginBottom: 10 }}>
            <div className="TileTitle">Reportés</div>
          </div>
          <div className="List" role="list">
            {grouped.postponed.slice(0, 50).map((a) => {
              const hasPublic = Boolean(String(a.public_token || '').trim());
              return (
              <div key={a.id} className="ListItem" role="listitem">
                <div style={{ minWidth: 0 }}>
                  <div className="UserName" style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <span className="Badge">Reporté</span>
                    <span className="OneLine" style={{ minWidth: 0 }}>
                      {contactTitle(a.client)}
                    </span>
                  </div>
                  <div className="Muted" style={{ marginTop: 6 }}>
                    {a.start_at ? `Proposition de date : ${formatDateTime(a.start_at)}` : 'Date à définir'} · {a.location_label || '—'}
                  </div>
                </div>
                {canAdminOps ? (
                  <div className="Badges">
                    {a.start_at ? (
                      <button
                        type="button"
                        className="Button"
                        disabled={quickActionId === Number(a.id)}
                        onClick={() => confirmProposedDate(a)}
                        title="Confirmer la proposition du client"
                      >
                        <span className="ButtonInline">
                          <IconCheck /> {quickActionId === Number(a.id) ? 'Confirmation…' : 'Confirmer'}
                        </span>
                      </button>
                    ) : null}
                    <button type="button" className="Button Button--ghost" onClick={() => openEditAppointment(a)}>
                      <span className="ButtonInline">
                        <IconEdit /> Modifier
                      </span>
                    </button>
                    <button type="button" className="Button Button--ghost" onClick={() => openCounterProposal(a)}>
                      <span className="ButtonInline">
                        <IconClock /> Contre-proposer
                      </span>
                    </button>
                    <button type="button" className="Button Button--ghost Button--danger Button--icon" title="Supprimer" aria-label="Supprimer" onClick={() => setModal({ type: 'delete', appointment: a, saving: false, error: '' })}>
                      <IconTrash />
                    </button>
                  </div>
                ) : (
                  <div className="Badges">
                    <button type="button" className="Button Button--ghost" disabled={!hasPublic} onClick={() => openPublicAppointment(a.public_token)}>
                      <span className="ButtonInline">
                        <IconEdit /> Modifier
                      </span>
                    </button>
                  </div>
                )}
              </div>
            );
            })}
          </div>
        </div>
      ) : null}

      {state.status === 'ready' && (grouped.past.length || grouped.cancelled.length || grouped.undated.length || grouped.other.length) ? (
        <div className="Card" style={{ marginTop: 12 }}>
          <div className="Row" style={{ marginBottom: 10 }}>
            <div className="TileTitle">Historique</div>
          </div>
          <div className="List" role="list">
            {grouped.cancelled.slice(0, 50).map((a) => (
              <div key={`cancel_${a.id}`} className="ListItem" role="listitem">
                <div style={{ minWidth: 0 }}>
                  <div className="UserName" style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <span className="Badge">Annulé</span>
                    <span className="OneLine" style={{ minWidth: 0 }}>
                      {contactTitle(a.client)}
                    </span>
                  </div>
                  <div className="Muted" style={{ marginTop: 6 }}>
                    {formatDateTime(a.start_at)} · {a.location_label || '—'}
                  </div>
                </div>
                {canAdminOps ? (
                  <div className="Badges">
                    <button type="button" className="Button Button--ghost Button--danger Button--icon" title="Supprimer" aria-label="Supprimer" onClick={() => setModal({ type: 'delete', appointment: a, saving: false, error: '' })}>
                      <IconTrash />
                    </button>
                  </div>
                ) : null}
              </div>
            ))}
            {grouped.past.slice(0, 50).map((a) => (
              <div key={a.id} className="ListItem" role="listitem">
                <div style={{ minWidth: 0 }}>
                  <div className="UserName OneLine">{contactTitle(a.client)}</div>
                  <div className="Muted" style={{ marginTop: 6 }}>
                    {formatDateTime(a.start_at)} · {a.location_label || '—'}
                  </div>
                </div>
                {canAdminOps ? (
                  <div className="Badges">
                    <button type="button" className="Button Button--ghost Button--danger Button--icon" title="Supprimer" aria-label="Supprimer" onClick={() => setModal({ type: 'delete', appointment: a, saving: false, error: '' })}>
                      <IconTrash />
                    </button>
                  </div>
                ) : null}
              </div>
            ))}
            {grouped.undated.slice(0, 20).map((a) => (
              <div key={`undated_${a.id}`} className="ListItem" role="listitem">
                <div style={{ minWidth: 0 }}>
                  <div className="UserName" style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <span className="Badge">Sans date</span>
                    <span className="OneLine" style={{ minWidth: 0 }}>
                      {contactTitle(a.client)}
                    </span>
                  </div>
                  <div className="Muted" style={{ marginTop: 6 }}>
                    {a.location_label || '—'}
                  </div>
                </div>
                {canAdminOps ? (
                  <div className="Badges">
                    <button type="button" className="Button Button--ghost Button--danger Button--icon" title="Supprimer" aria-label="Supprimer" onClick={() => setModal({ type: 'delete', appointment: a, saving: false, error: '' })}>
                      <IconTrash />
                    </button>
                  </div>
                ) : null}
              </div>
            ))}
            {grouped.other.slice(0, 20).map((a) => (
              <div key={`other_${a.id}`} className="ListItem" role="listitem">
                <div style={{ minWidth: 0 }}>
                  <div className="UserName" style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <span className="Badge">{String(a.status || '—')}</span>
                    <span className="OneLine" style={{ minWidth: 0 }}>
                      {contactTitle(a.client)}
                    </span>
                  </div>
                  <div className="Muted" style={{ marginTop: 6 }}>
                    {formatDateTime(a.start_at)} · {a.location_label || '—'}
                  </div>
                </div>
                {canAdminOps ? (
                  <div className="Badges">
                    <button type="button" className="Button Button--ghost Button--danger Button--icon" title="Supprimer" aria-label="Supprimer" onClick={() => setModal({ type: 'delete', appointment: a, saving: false, error: '' })}>
                      <IconTrash />
                    </button>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {modal?.type === 'new' ? (
        <Modal
          title="Nouveau rendez-vous"
          icon={<IconClock />}
          onClose={() => setModal(null)}
          footer={
            <>
              <button type="button" className="Button Button--ghost" onClick={() => setModal(null)} disabled={modal.saving}>
                Annuler
              </button>
              <button
                type="button"
                className="Button"
                disabled={
                  modal.saving ||
                  !String(modal.client_id || '') ||
                  !String(modal.date || '') ||
                  !String(modal.time || '') ||
                  (locationRequiresAddress(modal.location_type) && !String(modal.location_label || '').trim())
                }
                onClick={async () => {
                  if (modal.saving) return;
                  const c = resolveSelectedContact(modal.client_id);
                  if (!c) {
                    setModal((m) => ({ ...m, error: 'Choisis un contact.' }));
                    return;
                  }
                  const contactEmail = String(c.email || '').trim();
                  if (!contactEmail) {
                    setModal(null);
                    setModal({
                      type: 'email',
                      client: c,
                      email_to: '',
                      attach: true,
                      base: { ...modal },
                      saving: false,
                      error: '',
                    });
                    return;
                  }
                  setModal((m) => ({ ...m, saving: true, error: '' }));
                  const res = await confirmCreateAppointment({ ...modal, email_to: contactEmail, attach_email_to_contact: false });
                  if (!res.ok) {
                    setModal((m) => ({ ...m, saving: false, error: res.error || 'Erreur' }));
                    return;
                  }
                  const opened = maybeOpenAddressLinkModal(c, modal.location_type, modal.location_label);
                  if (!opened) setModal(null);
                }}
              >
                {modal.saving ? 'Enregistrement…' : 'Enregistrer'}
              </button>
            </>
          }
        >
          <div className="FormGrid">
            <div className="Field">
              <label className="Label">Contact</label>
              <select
                className="Select"
                value={String(modal.client_id || '')}
                onChange={(e) =>
                  setModal((m) => {
                    const nextClientId = e.target.value;
                    const selected = (contacts.list || []).find((x) => Number(x?.id) === Number(nextClientId)) || null;
                    const addr = contactAddress(selected);
                    return {
                      ...m,
                      client_id: nextClientId,
                      error: '',
                      location_label:
                        String(m.location_type || '') === 'client' && !String(m.location_label || '').trim() ? addr : m.location_label,
                    };
                  })
                }
                disabled={modal.saving || contacts.status !== 'ready'}
              >
                <option value="" disabled>
                  Choisir…
                </option>
                {(contacts.list || []).map((c) => (
                  <option key={c.id} value={String(c.id)}>
                    {contactTitle(c)}
                  </option>
                ))}
              </select>
              {contacts.status === 'loading' ? <div className="Muted" style={{ marginTop: 6 }}>Chargement des contacts…</div> : null}
              {contacts.status === 'ready' && (contacts.list || []).length === 0 ? <div className="Muted" style={{ marginTop: 6 }}>Aucun contact lié à ce site.</div> : null}
            </div>
            <div className="Field">
              <label className="Label">Date</label>
              <input className="Input" type="date" value={modal.date} onChange={(e) => setModal((m) => ({ ...m, date: e.target.value, error: '' }))} disabled={modal.saving} />
            </div>
            <div className="Field">
              <label className="Label">Heure</label>
              <input className="Input" type="time" value={modal.time} onChange={(e) => setModal((m) => ({ ...m, time: e.target.value, error: '' }))} disabled={modal.saving} />
            </div>
          </div>

          <div className="Field" style={{ marginTop: 10 }}>
            <label className="Label">Lieu</label>
            <div className="Badges" style={{ justifyContent: 'flex-start' }}>
              <button type="button" className={`TogglePill ${modal.location_type === 'agency' ? 'is-on' : ''}`} onClick={() => setModal((m) => ({ ...m, location_type: 'agency' }))} disabled={modal.saving}>
                En agence
              </button>
              <button
                type="button"
                className={`TogglePill ${modal.location_type === 'client' ? 'is-on' : ''}`}
                onClick={() =>
                  setModal((m) => {
                    const selected = resolveSelectedContact(m.client_id);
                    const addr = contactAddress(selected);
                    return {
                      ...m,
                      location_type: 'client',
                      location_label: String(m.location_label || '').trim() ? m.location_label : addr,
                    };
                  })
                }
                disabled={modal.saving}
              >
                Chez le contact
              </button>
              <button type="button" className={`TogglePill ${modal.location_type === 'other' ? 'is-on' : ''}`} onClick={() => setModal((m) => ({ ...m, location_type: 'other' }))} disabled={modal.saving}>
                Ailleurs
              </button>
            </div>
          </div>

          <div className="Field" style={{ marginTop: 10 }}>
            <label className="Label">
              Détails lieu {locationRequiresAddress(modal.location_type) ? '(requis)' : '(optionnel)'}
            </label>
            <input
              className="Input"
              value={String(modal.location_label || '')}
              onChange={(e) => setModal((m) => ({ ...m, location_label: e.target.value }))}
              disabled={modal.saving}
              placeholder={modal.location_type === 'other' ? 'Adresse / info…' : 'Laisser vide pour auto'}
              list={modal.location_type === 'client' ? 'appointments-address-suggest' : undefined}
            />
            {modal.location_type === 'client' && addressAutocomplete.status === 'loading' ? (
              <div className="Muted" style={{ marginTop: 6 }}>
                Suggestions d’adresse…
              </div>
            ) : null}
          </div>
          {modal.location_type === 'client' ? (
            <datalist id="appointments-address-suggest">
              {(addressAutocomplete.items || []).map((x, i) => (
                <option key={`addr_new_${i}`} value={x} />
              ))}
            </datalist>
          ) : null}

          {modal.error ? (
            <div className="Error" role="alert">
              {modal.error}
            </div>
          ) : null}
        </Modal>
      ) : null}

      {modal?.type === 'email' ? (
        <Modal
          title="Email manquant"
          icon={<IconUsers />}
          onClose={() => setModal(null)}
          footer={
            <>
              <button type="button" className="Button Button--ghost" onClick={() => setModal(null)} disabled={modal.saving}>
                Annuler
              </button>
              <button
                type="button"
                className="Button"
                disabled={modal.saving || !String(modal.email_to || '').trim()}
                onClick={async () => {
                  const email = String(modal.email_to || '').trim();
                  if (!email) return;
                  setModal((m) => ({ ...m, saving: true, error: '' }));
                  const res = await confirmCreateAppointment({ ...modal.base, email_to: email, attach_email_to_contact: Boolean(modal.attach) });
                  if (!res.ok) {
                    setModal((m) => ({ ...m, saving: false, error: res.error || 'Erreur' }));
                    return;
                  }
                  setModal(null);
                }}
              >
                {modal.saving ? 'Envoi…' : 'Envoyer + enregistrer'}
              </button>
            </>
          }
        >
          <div className="TitleMd" style={{ marginBottom: 6 }}>
            {contactTitle(modal.client)}
          </div>
          <div className="Muted" style={{ marginBottom: 10 }}>
            Ce contact n’a pas d’email enregistré. Indique un email de réception.
          </div>
          <div className="Field">
            <label className="Label">Email</label>
            <input className="Input" value={modal.email_to} onChange={(e) => setModal((m) => ({ ...m, email_to: e.target.value }))} disabled={modal.saving} />
          </div>
          <div className="Field">
            <label className="ButtonInline" style={{ gap: 10 }}>
              <input type="checkbox" checked={Boolean(modal.attach)} onChange={(e) => setModal((m) => ({ ...m, attach: Boolean(e.target.checked) }))} disabled={modal.saving} />
              <span>Enregistrer cet email sur le contact</span>
            </label>
          </div>
          <div className="Hint">
            Un email de confirmation sera envoyé à l’adresse ci-dessus.
          </div>
          {modal.error ? (
            <div className="Error" role="alert">
              {modal.error}
            </div>
          ) : null}
        </Modal>
      ) : null}

      {modal?.type === 'edit' ? (
        <Modal
          title="Modifier le rendez-vous"
          icon={<IconEdit />}
          onClose={() => setModal(null)}
          footer={
            <>
              <button type="button" className="Button Button--ghost" onClick={() => setModal(null)} disabled={modal.saving}>
                Fermer
              </button>
              <button
                type="button"
                className="Button"
                disabled={
                  modal.saving ||
                  (String(modal.status) === 'scheduled' && (!String(modal.date || '').trim() || !String(modal.time || '').trim())) ||
                  (String(modal.status) === 'postponed' && Boolean(modal.postponed_with_date) && (!String(modal.date || '').trim() || !String(modal.time || '').trim())) ||
                  (locationRequiresAddress(modal.location_type) && !String(modal.location_label || '').trim())
                }
                onClick={async () => {
                  if (modal.saving) return;
                  setModal((m) => ({ ...m, saving: true, error: '' }));
                  const res = await saveAppointmentEdit(modal);
                  if (!res.ok) {
                    setModal((m) => ({ ...m, saving: false, error: res.error || 'Erreur' }));
                    return;
                  }
                  await refresh();
                  if (res.email && res.email.ok === false) {
                    setModal({ type: 'email-warning', error: res.email.error || 'Email non envoyé' });
                    return;
                  }
                  const opened = maybeOpenAddressLinkModal(modal.client, modal.location_type, modal.location_label);
                  if (!opened) setModal(null);
                }}
              >
                {modal.saving ? 'Enregistrement…' : 'Enregistrer'}
              </button>
            </>
          }
        >
          <div className="TitleMd" style={{ marginBottom: 6 }}>
            {contactTitle(modal.client)}
          </div>

          <div className="Field">
            <label className="Label">Statut</label>
            <div className="Badges" style={{ justifyContent: 'flex-start' }}>
              <button
                type="button"
                className={`TogglePill ${String(modal.status) === 'scheduled' ? 'is-on' : ''}`}
                onClick={() =>
                  setModal((m) => {
                    const dt = defaultDateTimeLocal();
                    return {
                      ...m,
                      status: 'scheduled',
                      postponed_with_date: false,
                      date: String(m.date || '').trim() ? m.date : dt.date,
                      time: String(m.time || '').trim() ? m.time : dt.time,
                    };
                  })
                }
                disabled={modal.saving}
              >
                <IconClock /> Planifié
              </button>
              <button
                type="button"
                className={`TogglePill ${String(modal.status) === 'postponed' ? 'is-on' : ''}`}
                onClick={() =>
                  setModal((m) => ({
                    ...m,
                    status: 'postponed',
                    postponed_with_date: Boolean(String(m.date || '').trim() && String(m.time || '').trim()),
                  }))
                }
                disabled={modal.saving}
              >
                <IconBan /> Reporté
              </button>
              <button
                type="button"
                className={`TogglePill ${String(modal.status) === 'cancelled' ? 'is-on' : ''}`}
                onClick={() => setModal((m) => ({ ...m, status: 'cancelled', postponed_with_date: false }))}
                disabled={modal.saving}
              >
                <IconTrash /> Annulé
              </button>
            </div>
          </div>

          {String(modal.status) === 'scheduled' ? (
            <div className="FormGrid" style={{ marginTop: 10 }}>
              <div className="Field">
                <label className="Label">Date</label>
                <input className="Input" type="date" value={modal.date} onChange={(e) => setModal((m) => ({ ...m, date: e.target.value }))} disabled={modal.saving} />
              </div>
              <div className="Field">
                <label className="Label">Heure</label>
                <input className="Input" type="time" value={modal.time} onChange={(e) => setModal((m) => ({ ...m, time: e.target.value }))} disabled={modal.saving} />
              </div>
            </div>
          ) : (
            <>
              {String(modal.status) === 'postponed' ? (
                <div className="Notice" style={{ marginTop: 10 }}>
                  <div className="Badges" style={{ justifyContent: 'flex-start' }}>
                    <button
                      type="button"
                      className={`TogglePill ${Boolean(modal.postponed_with_date) ? 'is-on' : ''}`}
                      disabled={modal.saving}
                      onClick={() =>
                        setModal((m) => {
                          const dt = defaultDateTimeLocal();
                          return {
                            ...m,
                            status: 'postponed',
                            postponed_with_date: true,
                            date: String(m.date || '').trim() ? m.date : dt.date,
                            time: String(m.time || '').trim() ? m.time : dt.time,
                          };
                        })
                      }
                    >
                      Proposer une date
                    </button>
                    <button
                      type="button"
                      className={`TogglePill ${!Boolean(modal.postponed_with_date) ? 'is-on' : ''}`}
                      disabled={modal.saving}
                      onClick={() => setModal((m) => ({ ...m, status: 'postponed', postponed_with_date: false, date: '', time: '' }))}
                    >
                      Date ultérieure
                    </button>
                  </div>
                  {Boolean(modal.postponed_with_date) ? (
                    <div className="FormGrid" style={{ marginTop: 10 }}>
                      <div className="Field">
                        <label className="Label">Date proposée</label>
                        <input className="Input" type="date" value={modal.date} onChange={(e) => setModal((m) => ({ ...m, date: e.target.value }))} disabled={modal.saving} />
                      </div>
                      <div className="Field">
                        <label className="Label">Heure proposée</label>
                        <input className="Input" type="time" value={modal.time} onChange={(e) => setModal((m) => ({ ...m, time: e.target.value }))} disabled={modal.saving} />
                      </div>
                    </div>
                  ) : (
                    <div className="Hint" style={{ marginTop: 10 }}>
                      Le rendez-vous reste reporté sans date définie.
                    </div>
                  )}
                </div>
              ) : (
                <div className="Hint" style={{ marginTop: 10 }}>
                  {String(modal.status) === 'cancelled' ? 'Annulé : le rendez-vous n’aura pas lieu.' : ''}
                </div>
              )}
            </>
          )}

          <div className="Field" style={{ marginTop: 10 }}>
            <label className="Label">Lieu</label>
            <div className="Badges" style={{ justifyContent: 'flex-start' }}>
              <button type="button" className={`TogglePill ${modal.location_type === 'agency' ? 'is-on' : ''}`} onClick={() => setModal((m) => ({ ...m, location_type: 'agency' }))} disabled={modal.saving}>
                En agence
              </button>
              <button
                type="button"
                className={`TogglePill ${modal.location_type === 'client' ? 'is-on' : ''}`}
                onClick={() =>
                  setModal((m) => {
                    const addr = contactAddress(m.client);
                    return {
                      ...m,
                      location_type: 'client',
                      location_label: String(m.location_label || '').trim() ? m.location_label : addr,
                    };
                  })
                }
                disabled={modal.saving}
              >
                Chez le contact
              </button>
              <button type="button" className={`TogglePill ${modal.location_type === 'other' ? 'is-on' : ''}`} onClick={() => setModal((m) => ({ ...m, location_type: 'other' }))} disabled={modal.saving}>
                Ailleurs
              </button>
            </div>
          </div>

          <div className="Field" style={{ marginTop: 10 }}>
            <label className="Label">
              Détails lieu {locationRequiresAddress(modal.location_type) ? '(requis)' : '(optionnel)'}
            </label>
            <input
              className="Input"
              value={String(modal.location_label || '')}
              onChange={(e) => setModal((m) => ({ ...m, location_label: e.target.value }))}
              disabled={modal.saving}
              list={modal.location_type === 'client' ? 'appointments-address-suggest' : undefined}
            />
            {modal.location_type === 'client' && addressAutocomplete.status === 'loading' ? (
              <div className="Muted" style={{ marginTop: 6 }}>
                Suggestions d’adresse…
              </div>
            ) : null}
          </div>
          {modal.location_type === 'client' ? (
            <datalist id="appointments-address-suggest">
              {(addressAutocomplete.items || []).map((x, i) => (
                <option key={`addr_edit_${i}`} value={x} />
              ))}
            </datalist>
          ) : null}

          {modal.error ? (
            <div className="Error" role="alert">
              {modal.error}
            </div>
          ) : null}
        </Modal>
      ) : null}

      {modal?.type === 'link-address' ? (
        <Modal
          title="Lier l’adresse au contact"
          icon={<IconGlobe />}
          onClose={() => setModal(null)}
          footer={
            <>
              <button type="button" className="Button Button--ghost" onClick={() => setModal(null)} disabled={modal.saving}>
                Plus tard
              </button>
              <button
                type="button"
                className="Button"
                disabled={modal.saving || !String(modal.address_line1 || '').trim()}
                onClick={async () => {
                  const c = modal.contact || null;
                  if (!c) return;
                  setModal((m) => ({ ...m, saving: true, error: '' }));
                  const payload = {
                    id: Number(c.id),
                    first_name: String(c.first_name || ''),
                    last_name: String(c.last_name || ''),
                    company_name: String(c.company_name || ''),
                    siren: String(c.siren || ''),
                    siret: String(c.siret || ''),
                    email: String(c.email || ''),
                    phone_main: String(c.phone_main || ''),
                    phone_mobile: String(c.phone_mobile || ''),
                    address_line1: String(modal.address_line1 || ''),
                    address_line2: String(modal.address_line2 || ''),
                    postal_code: String(modal.postal_code || ''),
                    city: String(modal.city || ''),
                    country: String(modal.country || ''),
                    website: String(c.website || ''),
                    notes: String(c.notes || ''),
                    is_active: c.is_active == null ? true : Boolean(c.is_active),
                  };
                  const res = await api.adminClientUpdate(payload);
                  if (!res.ok) {
                    setModal((m) => ({ ...m, saving: false, error: res.error || 'Mise à jour impossible' }));
                    return;
                  }
                  setContacts((prev) => {
                    const list = Array.isArray(prev.list) ? prev.list : [];
                    const nextClient = res.client || payload;
                    return {
                      ...prev,
                      list: list.map((x) => (Number(x.id) === Number(payload.id) ? { ...x, ...nextClient } : x)),
                    };
                  });
                  setModal(null);
                }}
              >
                {modal.saving ? 'Enregistrement…' : 'Lier au contact'}
              </button>
            </>
          }
        >
          <div className="Muted" style={{ marginBottom: 10 }}>
            Adresse détectée depuis le rendez-vous: <strong>{String(modal.appointment_location || '').trim() || '—'}</strong>
          </div>
          <div className="FormGrid">
            <div className="Field">
              <label className="Label">Adresse (ligne 1)</label>
              <input className="Input" value={String(modal.address_line1 || '')} onChange={(e) => setModal((m) => ({ ...m, address_line1: e.target.value }))} />
            </div>
            <div className="Field">
              <label className="Label">Complément</label>
              <input className="Input" value={String(modal.address_line2 || '')} onChange={(e) => setModal((m) => ({ ...m, address_line2: e.target.value }))} />
            </div>
            <div className="Field">
              <label className="Label">Code postal</label>
              <input className="Input" value={String(modal.postal_code || '')} onChange={(e) => setModal((m) => ({ ...m, postal_code: e.target.value }))} />
            </div>
            <div className="Field">
              <label className="Label">Ville</label>
              <input className="Input" value={String(modal.city || '')} onChange={(e) => setModal((m) => ({ ...m, city: e.target.value }))} />
            </div>
            <div className="Field">
              <label className="Label">Pays</label>
              <input className="Input" value={String(modal.country || '')} onChange={(e) => setModal((m) => ({ ...m, country: e.target.value }))} />
            </div>
          </div>
          {modal.error ? (
            <div className="Error" role="alert" style={{ marginTop: 10 }}>
              {modal.error}
            </div>
          ) : null}
        </Modal>
      ) : null}

      {modal?.type === 'email-warning' ? (
        <Modal
          title="Email non envoyé"
          icon={<IconAlertTriangle />}
          onClose={() => setModal(null)}
          footer={
            <button type="button" className="Button Button--ghost" onClick={() => setModal(null)}>
              Fermer
            </button>
          }
        >
          <div className="Error" role="alert">
            {modal.error || 'Email non envoyé.'}
          </div>
        </Modal>
      ) : null}

      {modal?.type === 'delete' ? (
        <Modal
          title="Supprimer le rendez-vous"
          icon={<IconAlertTriangle />}
          onClose={() => setModal(null)}
          footer={
            <>
              <button type="button" className="Button Button--ghost" onClick={() => setModal(null)} disabled={modal.saving}>
                Annuler
              </button>
              <button
                type="button"
                className="Button Button--danger"
                disabled={modal.saving}
                onClick={async () => {
                  const id = Number(modal.appointment?.id);
                  if (!Number.isFinite(id) || id <= 0) return;
                  setModal((m) => ({ ...m, saving: true, error: '' }));
                  const res = await api.appointmentsDelete({ site_id: Number(siteId), id });
                  if (!res.ok) {
                    setModal((m) => ({ ...m, saving: false, error: res.error || 'Erreur' }));
                    return;
                  }
                  await refresh();
                  notifyAppointmentsChange('deleted');
                  setModal(null);
                }}
              >
                {modal.saving ? 'Suppression…' : 'Supprimer'}
              </button>
            </>
          }
        >
          <div className="Muted">
            Supprimer ce rendez-vous de l’historique ?
          </div>
          {modal.error ? (
            <div className="Error" role="alert" style={{ marginTop: 10 }}>
              {modal.error}
            </div>
          ) : null}
        </Modal>
      ) : null}

      {modal?.type === 'public' ? (
        <Modal
          title="Modifier le rendez-vous"
          icon={<IconClock />}
          onClose={() => setModal(null)}
          footer={
            <button type="button" className="Button Button--ghost" onClick={() => setModal(null)}>
              Fermer
            </button>
          }
        >
          <PublicAppointmentPage
            token={modal.token}
            embedded
            onChanged={async () => {
              try {
                await refresh();
                notifyAppointmentsChange('updated');
              } catch {
                // ignore
              }
            }}
          />
        </Modal>
      ) : null}
    </div>
  );
}
